import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useEdgeApi } from "@/composables/useEdgeApi";
import {
    isVmsStatusStreamable,
    vmsStatusUnavailableMessage,
    VMS_NO_VIDEO_MESSAGE,
} from "@/helpers/vmsCameraStatus";
import type {
    VMS,
    VMSDevice,
    MediaToken,
    StreamState,
    VMSListResponse,
    VMSDeviceListResponse,
    VMSDeviceResponse,
    HybridDevice,
    ClosestDevicesResponse,
    ClosestDevicesParams,
    ClosestGroup,
} from "@/types/vms";
import type { EdgeDevice } from "@/types/api";
import { resetSharedStreams } from "@/composables/useSharedCameraStream";

// The `params` value that asks the VMS for the camera's latest frame
const LIVE_STILL_PARAMS = "timestampMs=-1";

// How long a remembered still URL is worth trying before we mint a new one.
// Comfortably inside the hour the store treats a media token as valid, and long
// enough to still hit for a camera that has been streaming in the widget grid.
const THUMBNAIL_MAX_AGE_MS = 30 * 60 * 1000;

// Keyed on the pair that identifies the image endpoint, so two VMS servers
// handing out the same camera id can never share an entry.
function thumbnailCacheKey(vmsId: number, vmsCameraId: string): string {
    return `${vmsId}:${vmsCameraId}`;
}

export const useVMSStore = defineStore("vms", () => {
    const edgeApi = useEdgeApi();

    // Configuration state
    const availableVMS = ref<VMS[]>([]);
    const currentVMSId = ref<number | null>(null);

    // Authentication state
    const mediaToken = ref<MediaToken | null>(null);
    // Four players settling in the same tick each ask for a token, and the
    // server mints a fresh one per request. Concurrent callers share the
    // in-flight request instead; cleared once it settles so later calls still
    // refetch.
    let mediaTokenRequest: Promise<string> | null = null;

    // Stream state
    const activeStreams = ref<Map<string, StreamState>>(new Map());
    const streamRecoveryAttempts = ref<Map<string, number>>(new Map());

    // Cache state
    const deviceListCache = ref<Map<string, VMSDevice[]>>(new Map());
    // Whether the VMS device list has been asked for yet, however it went. A
    // camera missing from an empty cache means "not looked up" before the first
    // attempt and "no video" after it - and a failed lookup must land on the
    // second, or the grid would wait on an answer that is never coming.
    const vmsDevicesAttempted = ref(false);
    const lastFetchTimestamp = ref<Map<string, number>>(new Map());
    const VMS_DEVICE_LIST_TTL_MS = 60_000;
    const thumbnailCache = ref<Map<string, { url: string; timestamp: number }>>(
        new Map(),
    );

    // System state
    const loading = ref(false);
    const error = ref<string | null>(null);
    const dataReady = ref(false);

    /**
     * Fetch all available VMS systems
     */
    async function fetchVMSList(): Promise<VMS[]> {
        try {
            const response = await edgeApi.get<VMSListResponse>("cvi/vms");
            const vmsList = response.data?.data || response.data || [];
            availableVMS.value = Array.isArray(vmsList) ? vmsList : [];
            return availableVMS.value;
        } catch (e: any) {
            error.value = `Failed to fetch VMS list: ${e.message}`;
            console.error("Failed to fetch VMS list:", e);
            throw e;
        }
    }

    /**
     * Fetch all VMS devices from all VMS systems
     */
    async function fetchAllVMSDevices(): Promise<VMSDevice[]> {
        try {
            const response =
                await edgeApi.get<VMSDeviceListResponse>("cvi/vms/devices");
            const devices = response.data?.data || response.data || [];
            const deviceArray = Array.isArray(devices) ? devices : [];

            // Cache the results
            const cacheKey = "all-devices";
            deviceListCache.value.set(cacheKey, deviceArray);
            lastFetchTimestamp.value.set(cacheKey, Date.now());

            return deviceArray;
        } catch (e: any) {
            error.value = `Failed to fetch VMS devices: ${e.message}`;
            console.error("Failed to fetch VMS devices:", e);
            throw e;
        } finally {
            vmsDevicesAttempted.value = true;
        }
    }

    /**
     * Fetch devices for a specific VMS
     */
    async function fetchVMSDevices(vmsId: number): Promise<VMSDevice[]> {
        try {
            const response = await edgeApi.get<VMSDeviceListResponse>(
                `cvi/vms/${vmsId}/devices`,
            );
            const devices = response.data?.data || response.data || [];
            const deviceArray = Array.isArray(devices) ? devices : [];

            // Cache the results
            const cacheKey = `vms-${vmsId}-devices`;
            deviceListCache.value.set(cacheKey, deviceArray);
            lastFetchTimestamp.value.set(cacheKey, Date.now());

            return deviceArray;
        } catch (e: any) {
            error.value = `Failed to fetch VMS ${vmsId} devices: ${e.message}`;
            console.error(`Failed to fetch VMS ${vmsId} devices:`, e);
            throw e;
        }
    }

    /**
     * Fetch a specific VMS device
     */
    async function fetchVMSDevice(deviceId: string): Promise<VMSDevice> {
        try {
            const response = await edgeApi.get<VMSDeviceResponse>(
                `cvi/vms/devices/${deviceId}`,
            );
            const device = response.data?.data || response.data;

            if (!device) {
                throw new Error(`Device ${deviceId} not found`);
            }

            return device;
        } catch (e: any) {
            error.value = `Failed to fetch VMS device ${deviceId}: ${e.message}`;
            console.error(`Failed to fetch VMS device ${deviceId}:`, e);
            throw e;
        }
    }

    /**
     * Cameras ranked by proximity to active incident markers.
     *
     * Call form matters: every parameter goes in the QUERY STRING. The route is
     * `cvi/vms/closestdevices` with no path segments — an earlier build took
     * `/{page}/{pageSize}` in the path and ignored them, and that form now 404s.
     *
     * The response envelope returns `data: null` (not `[]`) past the last page,
     * hence the `?? []`.
     */
    async function fetchClosestDevices(params: ClosestDevicesParams = {}): Promise<{
        rows: ClosestGroup[];
        total: number;
        page: number;
    }> {
        const { page = 1, pageSize = 4, venueId, levelId, incidentId, pattern } =
            params;

        const query: Record<string, number | string> = { page, pageSize };
        if (venueId != null) query.venueId = venueId;
        if (levelId != null) query.levelId = levelId;
        if (incidentId != null) query.incidentId = incidentId;
        if (pattern) query.pattern = pattern;

        const response = await edgeApi.get<ClosestDevicesResponse>(
            "cvi/vms/closestdevices",
            { params: query },
        );

        return {
            rows: response.data?.data ?? [],
            total: response.data?.total ?? 0,
            page: response.data?.page ?? page,
        };
    }

    /**
     * Get media token for stream authentication
     *
     * Concurrent callers share one request: without this, four players starting
     * at once produce as many `GET cvi/mediatoken` calls as they have retries,
     * each minting a brand new token.
     */
    async function fetchMediaToken(): Promise<string> {
        // Check if we have a valid cached token
        if (mediaToken.value && isMediaTokenValid()) {
            return mediaToken.value.token;
        }

        if (!mediaTokenRequest) {
            mediaTokenRequest = requestMediaToken().finally(() => {
                mediaTokenRequest = null;
            });
        }

        return await mediaTokenRequest;
    }

    /**
     * Perform the actual media token request (always hits the network)
     */
    async function requestMediaToken(): Promise<string> {
        try {
            const response = await edgeApi.get("cvi/mediatoken");
            let tokenValue = "";

            // Handle different response formats
            if (typeof response.data === "string") {
                tokenValue = response.data.trim();
            } else if (response.data?.token) {
                tokenValue = response.data.token;
            } else if (response.data?.access_token) {
                tokenValue = response.data.access_token;
            } else if (response.data?.value) {
                tokenValue = response.data.value;
            }

            if (!tokenValue) {
                throw new Error("Media token response was empty or malformed");
            }

            // Cache the token
            mediaToken.value = {
                token: tokenValue,
                fetchedAt: Date.now(),
                expiresAt: undefined, // VMS tokens don't typically provide expiration
            };

            return tokenValue;
        } catch (e: any) {
            // Check for 429 rate limiting
            if (e.response?.status === 429 || e.message?.includes("429")) {
                error.value =
                    "Rate limited while fetching media token - retrying automatically";
                console.warn("Media token fetch rate limited:", e);
                throw new Error("429 Too Many Requests - media token");
            } else {
                error.value = `Failed to fetch media token: ${e.message}`;
                console.error("Failed to fetch media token:", e);
                throw e;
            }
        }
    }

    /**
     * Check if the current media token is valid
     */
    function isMediaTokenValid(): boolean {
        if (!mediaToken.value) return false;

        // If no expiration is set, consider token valid for 1 hour
        const maxAge = 60 * 60 * 1000; // 1 hour in milliseconds
        const tokenAge = Date.now() - mediaToken.value.fetchedAt;

        if (mediaToken.value.expiresAt) {
            return Date.now() < mediaToken.value.expiresAt;
        }

        return tokenAge < maxAge;
    }

    /**
     * Ensure we have a valid media token
     */
    async function ensureMediaToken(): Promise<string> {
        if (mediaToken.value && isMediaTokenValid()) {
            return mediaToken.value.token;
        }
        return await fetchMediaToken();
    }

    /**
     * Generate stream URL for a VMS device
     */
    function getStreamUrl(
        vmsId: number,
        deviceId: string,
        token: string,
        format: string = "mp4",
        params: string = "none",
    ): string {
        const baseUrl = edgeApi.defaults?.baseURL || "";
        const cacheBuster = Date.now();
        return `${baseUrl}/cvi/vms/${vmsId}/devices/${deviceId}/stream?token=${encodeURIComponent(token)}&format=${format}&params=${params}&_=${cacheBuster}`;
    }

    /**
     * Generate image URL for a VMS device
     *
     * Every still request in the app funnels through here, so this is also where
     * the per-camera thumbnail cache is written. The cache is a "last known
     * still" index: a surface that opens cold (the camera dialog) can paint the
     * remembered URL instantly - the browser already has that image decoded from
     * the player that requested it - instead of a black video element.
     */
    function getImageUrl(
        vmsId: number,
        deviceId: string,
        token: string,
        params: string = LIVE_STILL_PARAMS,
    ): string {
        const baseUrl = edgeApi.defaults?.baseURL || "";
        const cacheBuster = Date.now();
        const url = `${baseUrl}/cvi/vms/${vmsId}/devices/${deviceId}/image?token=${encodeURIComponent(token)}&params=${params}&_=${cacheBuster}`;

        // Only live stills are remembered. A recorded still is the frame at one
        // specific position, so replaying it as the preview of a live view would
        // show the operator the wrong moment.
        if (params === LIVE_STILL_PARAMS) {
            thumbnailCache.value.set(thumbnailCacheKey(vmsId, deviceId), {
                url,
                timestamp: Date.now(),
            });
        }

        return url;
    }

    /**
     * Last known live still for a camera, or undefined when there is none worth
     * trying. The URL is keyed to the camera, so it can only ever preview that
     * camera - the wrong-feed risk that makes the player drop its poster on
     * stopStream() does not exist here.
     *
     * Re-requesting a remembered URL returns the camera's *current* frame (the
     * server reads timestampMs=-1 as "latest", verified against dev), so a hit
     * is either instant from the browser's image cache or a fresh still - never
     * a stale one served from disk. The age bound only exists because the token
     * baked into the URL eventually stops being accepted.
     */
    function getCachedThumbnail(
        vmsId: number,
        vmsCameraId: string,
        maxAgeMs: number = THUMBNAIL_MAX_AGE_MS,
    ): string | undefined {
        const entry = thumbnailCache.value.get(
            thumbnailCacheKey(vmsId, vmsCameraId),
        );
        if (!entry) return undefined;
        return Date.now() - entry.timestamp <= maxAgeMs ? entry.url : undefined;
    }

    /**
     * Initialize VMS store
     */
    async function initializeVMS(): Promise<void> {
        if (dataReady.value) return;

        loading.value = true;
        error.value = null;

        try {
            await fetchVMSList();
            dataReady.value = true;
        } catch (e: any) {
            error.value = e.message;
            console.error("Failed to initialize VMS:", e);
        } finally {
            loading.value = false;
        }
    }

    /**
     * Match devices from /devices endpoint with VMS devices for streaming capabilities
     * @param devices - Array of devices from /devices endpoint
     * @returns Array of devices with merged VMS streaming information
     */
    function matchDevicesWithVMS(devices: EdgeDevice[]): HybridDevice[] {
        // The edge device list lands first on a cold load, so until the VMS
        // list is in, "no match" means "not looked up yet". Reporting that as
        // no video put NO VIDEO on every tile for the moment in between.
        const lookedUp =
            deviceListCache.value.has("all-devices") ||
            vmsDevicesAttempted.value;
        const allVMSDevices = deviceListCache.value.get("all-devices") || [];

        return devices.map((device) => {
            // Find matching VMS device by device.dbId (numeric ID)
            const vmsDevice = allVMSDevices.find(
                (vms) => vms.id === device.dbId,
            );

            if (vmsDevice) {
                // A camera the VMS knows about is only streamable while the
                // VMS reports it as such - streaming an offline camera just
                // spins forever.
                const streamable = isVmsStatusStreamable(vmsDevice.status);

                // Merge VMS streaming capabilities with device data
                return {
                    ...device,
                    vms: {
                        available: streamable,
                        vmsId: vmsDevice.vmsId,
                        vmsCameraId: vmsDevice.vmsCameraId,
                        vendor: vmsDevice.vendor,
                        model: vmsDevice.model,
                        status: vmsDevice.status,
                        version: vmsDevice.version,
                        info: vmsDevice.info,
                        reason: streamable
                            ? undefined
                            : vmsStatusUnavailableMessage(vmsDevice.status),
                    },
                };
            } else if (!lookedUp) {
                return { ...device, vms: { available: false, pending: true } };
            } else {
                // Device exists in /devices but not in VMS
                return {
                    ...device,
                    vms: {
                        available: false,
                        reason: VMS_NO_VIDEO_MESSAGE,
                    },
                };
            }
        });
    }

    /** Whether the cached VMS device list is recent enough to skip a refetch. */
    function isVmsDeviceListFresh(): boolean {
        const fetchedAt = lastFetchTimestamp.value.get("all-devices");
        return (
            fetchedAt !== undefined &&
            Date.now() - fetchedAt < VMS_DEVICE_LIST_TTL_MS
        );
    }

    /**
     * Fetch a single device from /devices endpoint and create hybrid with VMS data
     */
    async function fetchHybridDevice(
        deviceId: string,
    ): Promise<HybridDevice | null> {
        try {
            // Fetch the device from /devices endpoint
            const response = await edgeApi.get(`devices/${deviceId}`, {
                params: { type: "camera" },
            });
            const deviceData = response?.data as EdgeDevice;

            if (!deviceData) {
                return null;
            }

            // Only block on the VMS device list when we have nothing to match
            // against. cvi/* proxies to the VMS, which holds requests open once
            // its session pool is full, so awaiting a list we already have is
            // what stopped the viewer opening at all while streams were running.
            if (!deviceListCache.value.get("all-devices")?.length) {
                await fetchAllVMSDevices();
            } else if (!isVmsDeviceListFresh()) {
                void fetchAllVMSDevices().catch(() => {
                    // Background refresh; the cached list still answers below.
                });
            }

            // Convert to hybrid device
            const hybridDevices = matchDevicesWithVMS([deviceData]);
            return hybridDevices[0] || null;
        } catch (e: any) {
            error.value = `Failed to fetch device ${deviceId}: ${e.message}`;
            console.error(`Failed to fetch device ${deviceId}:`, e);
            return null;
        }
    }

    /**
     * Get VMS streaming information for a specific device
     */
    function getVMSInfoForDevice(
        deviceId: number,
    ): { vmsId: number; vmsCameraId: string } | null {
        const allVMSDevices = deviceListCache.value.get("all-devices") || [];
        const vmsDevice = allVMSDevices.find((vms) => vms.id === deviceId);

        if (vmsDevice) {
            return {
                vmsId: vmsDevice.vmsId,
                vmsCameraId: vmsDevice.vmsCameraId,
            };
        }

        return null;
    }

    /**
     * Clear all cached data
     */
    function clearCache(): void {
        deviceListCache.value.clear();
        vmsDevicesAttempted.value = false;
        lastFetchTimestamp.value.clear();
        thumbnailCache.value.clear();
        activeStreams.value.clear();
        streamRecoveryAttempts.value.clear();
    }

    /**
     * Reset store to initial state
     */
    function $reset(): void {
        availableVMS.value = [];
        currentVMSId.value = null;
        mediaToken.value = null;
        // Drop the shared request too, so a caller after a reset cannot be
        // handed a token minted for the previous session
        mediaTokenRequest = null;
        clearCache();
        resetSharedStreams();
        loading.value = false;
        error.value = null;
        dataReady.value = false;
    }

    // Computed getters
    const getAvailableVMS = computed(() => availableVMS.value);
    const getCurrentVMSId = computed(() => currentVMSId.value);
    const getMediaToken = computed(() => mediaToken.value);
    const getActiveStreams = computed(() => activeStreams.value);
    const getDeviceCache = computed(() => deviceListCache.value);
    const getThumbnailCache = computed(() => thumbnailCache.value);
    const isLoadingVMS = computed(() => loading.value);
    const getVMSError = computed(() => error.value);
    const isVMSReady = computed(() => dataReady.value);

    return {
        // State
        availableVMS,
        currentVMSId,
        mediaToken,
        activeStreams,
        streamRecoveryAttempts,
        deviceListCache,
        lastFetchTimestamp,
        thumbnailCache,
        loading,
        error,
        dataReady,

        // Actions
        fetchVMSList,
        fetchAllVMSDevices,
        fetchVMSDevices,
        fetchVMSDevice,
        fetchClosestDevices,
        fetchHybridDevice,
        fetchMediaToken,
        ensureMediaToken,
        initializeVMS,
        matchDevicesWithVMS,
        getVMSInfoForDevice,
        clearCache,
        $reset,

        // Helpers
        isMediaTokenValid,
        getStreamUrl,
        getImageUrl,
        getCachedThumbnail,

        // Getters
        getAvailableVMS,
        getCurrentVMSId,
        getMediaToken,
        getActiveStreams,
        getDeviceCache,
        getThumbnailCache,
        isLoadingVMS,
        getVMSError,
        isVMSReady,
    };
});
